import React from 'react';
import {Checkbox, Form, InputNumber} from "antd";
import {Frequencies, PersonalMaritalStatuses} from "../../../../../../../../models";
import {
    buildInitialValues,
    containerStyle, FormItem,
    MethodologicalRow,
    StripedCard
} from "../LenderTestFiguresEditor";
import {Figure} from "../Figure";
import {CheckCircleFilled, ExclamationCircleFilled} from "@ant-design/icons";
import {green, orange} from "@ant-design/colors";


function applicantFigures(mortgage, applicant, jobId, jobsIndex = 0) {
    const appN = `applicant${applicant}`;
    const app = mortgage[appN];
    const job = app.employmentIncome.find(it=>it.id === jobId);
    const grossBasic = () => {
        const fig = new Figure({
            label: 'Gross Basic Salary',
            disabled: false,
            namesPath: [appN, 'jobs', jobsIndex, 'basic', 'yearMostRecent'],
            provable: true,
            float: false,
        })
        const section = mortgage.uploadSections.find(it => it.name === `salary-cert-${applicant}` && it.applicant === applicant && it.index === jobId);
        const mostRecentUpload = section?.verifiableUploads?.reduce((latest, current) => {
            return (!latest || (current.values.documentDate > latest.values.documentDate)) ? current : latest;
        }, null);

        if (mostRecentUpload && mostRecentUpload.values.grossBasicThisYear) {
            fig.addMethod({
                description: `Gross Basic from Salary Certificate`,
                amount: mostRecentUpload.values.grossBasicThisYear,
                claimed: job.grossBasic,
                uploadID: mostRecentUpload.id,
                operator: '=',
            })
            fig.setValue(mostRecentUpload.values.grossBasicThisYear, 'Verified gross basic');
        } else {

            fig.setAlert('No gross basic in salary certificate upload')
            fig.setValue(job.grossBasic, 'Claimed gross basic');
        }
        return fig;
    }
    const childrensAllowance = () => {

        const fig = new Figure({
            label: 'Childrens Allowance',
            disabled: !app.personal.dependentDOBs?.length,
            namesPath: [appN, 'monthlyIncomes', 'childrenAllowance'],
            provable: false,
            float: false,
        })
        const claimed = app.financial.monthlyChildrenAllowance || 0
        fig.setValue(claimed, 'Claimed childrens allowance');
        return fig;
    }
    const otherAllowances = () => {
        const fig = new Figure({
            label: 'Other Allowances',
            disabled: !app.financial.monthlyOtherAllowances,
            namesPath: [appN, 'monthlyIncomes', 'otherAllowances'],
            provable: false,
            float: false,
        })
        const claimed = app.financial.monthlyOtherAllowances || 0
        fig.setValue(claimed, 'Claimed other allowances');
        return fig;
    }

    const pensionDeduction = () =>{

        const payslips = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `payslips-${applicant}` && s.isActive && s.index === jobId)

        const pensionUpload = (payslips?.uploads || []).filter(u=> u.isActive && u.isVerifiable && u.values && u.values.pensionDeduction).sort((a,b)=>{
            return b.values.documentDate.diff(a.values.documentDate)
        })[0]

        const fig = new Figure({
            label: 'Pension Contribution',
            disabled: !pensionUpload,
            namesPath: [appN, 'jobs', jobsIndex, 'yearlyPensionContribution'],
            provable: !!pensionUpload,
            float: false,
        })

        if (pensionUpload){

            const factor = (()=> {
                    switch (job.payFrequency){
                        case Frequencies.WEEKLY:
                            return 52
                        case Frequencies.FORTNIGHTLY:
                            return job.publicSector ? 26.09 : 26
                        case Frequencies.MONTHLY:
                            return 12
                        case Frequencies.QUARTERLY:
                            return 4
                        case Frequencies.YEARLY:
                            return 1
                        default:
                            return 0
                    }
                }
            )()
            const annualPension = Math.round(pensionUpload.values.pensionDeduction * factor * 100) / 100
            fig.addMethod({
                description: `Pension Deduction from Payslip (${factor} x ${pensionUpload.values.pensionDeduction})`,
                amount: annualPension,
                claimed: 0,
                uploadID: pensionUpload.id,
                operator: '=',
            })
            fig.setValue(annualPension, 'Verified pension contribution');
        }
        else {
            fig.setAlert('No pension deduction in payslips uploads')
            fig.setValue(0, 'Claimed pension contribution');
        }
        return fig;
    }

    function variableIncomes() {
        const incomeMap = {
            shiftAllowance: 'Shift Allowance',
            carAllowance: 'Car Allowance',
            otherAllowance: 'Other Allowance',
            overtime: 'Overtime',
            bonus: 'Bonus',
            commission: 'Commission'
        }
        const salaryCertSection = mortgage.uploadSections.find(it => it.name === `salary-cert-${applicant}` && it.applicant === applicant && it.index === job.id);
        const mostRecentUpload = salaryCertSection?.verifiableUploads?.reduce((latest, current) => {
            return (!latest || (current.values.documentDate > latest.values.documentDate)) ? current : latest;
        }, null);

        return Object.keys(incomeMap).flatMap((key) => {

            // Variable Income Year Most Recent
            let claimed = (job.incomes || []).find(it => it?.name === key)?.yearMostRecent || 0;

            let valuesKey = key + 'YearMostRecent'
            let verified = mostRecentUpload && mostRecentUpload.values[valuesKey] ? mostRecentUpload.values[valuesKey] : 0;

            const fig1 = new Figure({
                label: incomeMap[key] + ' Y Most Recent',
                disabled: !claimed && !verified,
                namesPath: [appN, 'jobs', jobsIndex, key, 'yearMostRecent'],
                provable: true,
                float: false,
            })
            if (verified) {

                fig1.addMethod({
                    description: `${incomeMap[key]} from Salary Certificate`,
                    amount: verified,
                    claimed,
                    uploadID: mostRecentUpload.id,
                    operator: '=',
                })
                fig1.setValue(verified, `Verified ${incomeMap[key]}`);
                if (verified === 0){
                    fig1.setAlert(`No ${incomeMap[key]} in most recent upload`)
                }
            } else {
                if (claimed){
                    fig1.setAlert(`No ${incomeMap[key]} in most recent upload`)
                    fig1.setValue(claimed, `Claimed ${incomeMap[key]}`);
                }

            }

            // Variable Income Last Year
            claimed = (job.incomes || []).find(it => it.name === key)?.yearBefore || 0;
            valuesKey = key + 'YearBefore'
            verified = mostRecentUpload && mostRecentUpload.values[valuesKey] ? mostRecentUpload.values[valuesKey] : 0;
            const fig2 = new Figure({
                label: incomeMap[key] + ' Y Before',
                disabled: !claimed && !verified,
                namesPath: [appN, 'jobs', jobsIndex, key, 'yearBefore'],
                provable: true,
                float: false,
            })
            if (verified) {
                fig2.addMethod({
                    description: `${incomeMap[key]} from Salary Certificate`,
                    amount: verified,
                    claimed,
                    uploadID: mostRecentUpload.id,
                    operator: '=',
                })
                fig2.setValue(verified, `Verified ${incomeMap[key]}`);
            } else {
                if (claimed){
                    fig2.setAlert(`No ${incomeMap[key]} in most recent upload`)
                    fig2.setValue(claimed, `Claimed ${incomeMap[key]}`);
                }

            }


            // Variable Income Year Before That
            claimed = (job.incomes || []).find(it => it.name === key)?.yearBeforeThat || 0;
            valuesKey = key + 'YearBeforeThat'
            verified = mostRecentUpload && mostRecentUpload.values[valuesKey] ? mostRecentUpload.values[valuesKey] : 0;

            const fig3 = new Figure({
                label: incomeMap[key] + ' Y Before That',
                disabled: !claimed && !verified,
                namesPath: [appN, 'jobs', jobsIndex, key, 'yearBeforeThat'],
                provable: true,
                float: false,
            })
            if (mostRecentUpload && mostRecentUpload.values[key + 'YearBeforeThat']) {
                fig3.addMethod({
                    description: `${incomeMap[key]} from Salary Certificate`,
                    amount: mostRecentUpload.values[key + 'YearBeforeThat'],
                    claimed,
                    uploadID: mostRecentUpload.id,
                    operator: '=',
                })
                fig3.setValue(verified, `Verified ${incomeMap[key]}`);
            } else {
                if (claimed){
                    fig3.setAlert(`No ${incomeMap[key]} in most recent upload`)
                    fig3.setValue(claimed, `Claimed ${incomeMap[key]}`);
                }

            }

            claimed = (job.incomes || []).find(it => it.name === key)?.guaranteed || false;
            valuesKey = key + 'Guaranteed'
            verified = mostRecentUpload && mostRecentUpload.values[valuesKey] ? mostRecentUpload.values[valuesKey] : 0;

            const fig4 = new Figure({
                label: incomeMap[key] + ' G\'teed',
                disabled: !claimed && !verified,
                namesPath: [appN, 'jobs', jobsIndex, key, 'guaranteed'],
                provable: true,
                float: false,
            })
            if (mostRecentUpload && mostRecentUpload.values[valuesKey]) {
                fig4.addMethod({
                    description: `${incomeMap[key]} from Salary Certificate`,
                    amount: mostRecentUpload.values[valuesKey],
                    claimed,
                    uploadID: mostRecentUpload.id,
                    operator: '=',
                })
                fig4.setValue(verified, `Verified G'teed ${incomeMap[key]}`);
            } else {
                if (claimed){
                    fig4.setAlert(`No ${incomeMap[key]} in most recent upload`)
                    fig4.setValue(claimed, `Claimed ${incomeMap[key]} G'teed`);
                }

            }
            return [fig1, fig2, fig3, fig4];


        })

    }


    return [
        grossBasic(),
        ...variableIncomes(),
        pensionDeduction(),
        childrensAllowance(),
        otherAllowances()
    ]
}

function Applicant({showingZeros, firstName, applicantNumber, colorSet, mortgage, openUpload}) {
    const app = `applicant${applicantNumber}`

    // const lastYear = new Date().getFullYear() - 1;
    //
    //
    // const applicant = applicantFigures(mortgage, applicantNumber)
    //
    //
    // const gorssBasic = applicant.find(it => it.namesPath.includes('basic') && it.namesPath.includes('yearMostRecent'));

     const generateChildren = (open) => {

       return mortgage[app].employmentIncome.map((job, index) => {
            const jobFigures =  applicantFigures(mortgage, applicantNumber, job.id, index)

           const jobGrossBasic = jobFigures.find(it => it.namesPath.includes('basic') && it.namesPath.includes('yearMostRecent'));

            const pensionContribution = jobFigures.find(it => it.namesPath.includes('yearlyPensionContribution'));
            const additionalItems = [
                {
                    type: 'select',
                    label: job.currentEmployerName ? `(${job.currentEmployerName}) Sector` : `Sector`,
                    name: [app, "jobs", index, "sector"],
                    options: [
                        {label: "PUBLIC", value: "PUBLIC"},
                        {label: "PRIVATE", value: "PRIVATE"},
                        {label: "SELF", value: "SELF"}
                    ]
                },
            ]
            return (
                <React.Fragment key={index}>
                    {
                        additionalItems.map((item, i) => (<FormItem type={item.type} label={item.label} name={item.name} options={item.options} showingZeros={open} key={index}/>))
                    }
                    <MethodologicalRow item={jobGrossBasic} openUpload={openUpload} color={colorSet.even}/>
                    {(!pensionContribution.disabled || open) && (
                        <MethodologicalRow item={pensionContribution} openUpload={openUpload} color={colorSet.odd}/>
                    )}

                    <ApplicantVariableIncomePanel key={JSON.stringify(jobFigures)} openUpload={openUpload} figures={jobFigures} colorSet={colorSet} expanded={open}/>
                    <ApplicantPayscalePanel applicant={applicantNumber} jobIndex={index} colorSet={colorSet}/>

                </React.Fragment>
            )

        })
    }

    return (
        <div className="d-col gap-6">
            <StripedCard
                title={firstName}
                colors={colorSet}
                generateChildren={generateChildren}
                items={[
                    {
                        type: 'select',
                        label: 'Marital Status',
                        name: [app, "maritalStatus"],
                        options: Object.entries(PersonalMaritalStatuses).map(([key, value]) => ({
                            label: value,
                            value: value
                        })).filter(it => it.value !== PersonalMaritalStatuses.COHABITING)
                    },
                    {
                        type: 'number',
                        label: 'Rounded Age',
                        name: [app, "age"]
                    },
                    {
                        type: 'boolean',
                        label: 'Works',
                        name: [app, "works"]
                    },
                    {
                        type: 'number',
                        label: 'Childrens Allowance',
                        name: [app, "monthlyIncomes", "childrensAllowance"]
                    },
                    {
                        type: 'number',
                        label: 'Other Welfare/Allowances',
                        name: [app, "monthlyIncomes", "otherAllowance"]
                    },


                ]}/>

        </div>

    );
}

function ApplicantJobsPanel({mortgage, firstName, colorSet, applicantFigures, applicantNumber}) {
    const app = `applicant${applicantNumber}`
    const applicant = mortgage[app];
    const jobs = applicant.employmentIncome;
    return (<div className="d-col gap-6 ps-12">
            {jobs.map((job, index) => {
                return (<StripedCard
                    key={index}
                    title={`Job ${index + 1} - ${job.employerName || 'Unnamed Employer'}`}
                    colors={colorSet}
                    items={[
                        {
                            type: 'select',
                            label: 'Sector',
                            name: [app, "jobs", index, "sector"],
                            options: [
                                {label: "PUBLIC", value: "PUBLIC"},
                                {label: "PRIVATE", value: "PRIVATE"},
                                {label: "SELF", value: "SELF"}
                            ]
                        },
                        {
                            type: 'number',
                            label: 'Gross Basic Salary',
                            name: [app, 'jobs', index, 'basic', 'yearMostRecent'],
                        },
                        {
                            type: 'number',
                            label: 'Pension Contribution',
                            name: [app, 'jobs', index, 'yearlyPensionContribution'],
                        },

                    ]}/>)
            })}

    </div>
    )
}

function ApplicantPayscalePanel({applicant, jobIndex=0, colorSet}) {
    return (<div style={{...containerStyle, backgroundColor: colorSet.odd,}}>
            <table>
                <colgroup>
                    <col style={{width: '40%'}}/>
                    <col style={{width: '20%'}}/>
                    <col style={{width: '20%'}}/>
                    <col style={{width: '20%'}}/>

                </colgroup>
                <tbody>
                <tr>
                    <td>
                        Next Salary on Payscale
                    </td>
                    <td>
                        <Form.Item noStyle name={[`applicant${applicant}`, 'jobs', jobIndex, 'payScale', 0]}>
                            <InputNumber size="small" style={{width: '100%'}}/>
                        </Form.Item>
                    </td>
                    <td>
                        <Form.Item noStyle name={[`applicant${applicant}`, 'jobs', jobIndex, 'payScale', 1]}>
                            <InputNumber size="small" style={{width: '100%'}}/>
                        </Form.Item>
                    </td>
                    <td>
                        <Form.Item noStyle name={[`applicant${applicant}`, 'jobs', jobIndex, 'payScale', 2]}>
                            <InputNumber size="small" style={{width: '100%'}}/>
                        </Form.Item>
                    </td>


                </tr>
                </tbody>
            </table>
        </div>
    )
}
function ApplicantVariableIncomePanel({figures, openUpload, colorSet, expanded}) {

    const initValues = buildInitialValues(figures);
    const variableIncome = {
        shiftAllowance: 'Shift Allowance',
        carAllowance: 'Car Allowance',
        otherAllowance: 'Other Allowance',
        overtime: 'Overtime',
        bonus: 'Bonus',
        commission: 'Commission'
    }


    //TODO: where is the guaranteed?
    return Object.keys(variableIncome).map((key, i) => (
        <MethodologicalVariableIncomeRow key={key} color={i % 2 === 0 ? colorSet.even : colorSet.odd}
                                         colorSet={colorSet} type={key} values={initValues} label={variableIncome[key]}
                                         figures={figures} expanded={expanded}/>
    ))
}

function MethodologicalVariableIncomeRow({type, open, openUpload, figures, values, item, color, i, label, expanded, warning}) {
    const containerStyle = {
        display: "grid",
        backgroundColor: color,
        borderRadius: "4px",
        padding: "4px",
        margin: "4px"
    }

    // const caretStyle = {
    //     cursor: 'pointer',
    //     transform: open ? '' : 'rotate(90deg)',
    //     color: open ? 'blue' : 'black',
    //     transition: 'transform 0.2s, color 0.2s',
    // }
    // const alignCellStyle = {
    //     textAlign: 'right'
    // }
    //const alert = item?.alert || item.methodology.find(row => row.warning)?.warning
    //const success = item.success

    const myItems = figures.filter(it => it.namesPath.includes(type));
    const yearMostRecent = myItems.find(it => it.namesPath.includes('yearMostRecent'));
    const yearBefore = myItems.find(it => it.namesPath.includes('yearBefore'));
    const yearBeforeThat = myItems.find(it => it.namesPath.includes('yearBeforeThat'));
    const guaranteed = myItems.find(it => it.namesPath.includes('guaranteed'));

    const allVerified = myItems.every(it => {
        return !it.alert && !it.methodology.find(row => row.warning)
    })
    const noEntries = myItems.every(it => {
        return !it.value && !it.claimed
    })
    const hidden = !expanded && noEntries

    if (hidden) {
        return null
    }
    return (<div style={containerStyle}>
        <table>
            <colgroup>
                <col style={{width: '34%'}}/>
                <col style={{width: '6%'}}/>
                <col style={{width: '17%'}}/>
                <col style={{width: '17%'}}/>
                <col style={{width: '17%'}}/>
                <col style={{width: '9%'}}/>
            </colgroup>
            <tbody>
            <tr>
                <td style={{fontWeight: open ? 'bold' : 'normal'}}>
                    {label}
                </td>
                <td>
                    {allVerified ? <CheckCircleFilled style={{color: green.primary}}/> :
                        <ExclamationCircleFilled style={{color: orange.primary}}/>}
                </td>
                <td>
                    <Form.Item noStyle name={yearMostRecent.namesPath}>
                        <InputNumber size="small" style={{width: '100%'}}/>
                    </Form.Item>
                </td>
                <td>
                    <Form.Item noStyle name={yearBefore.namesPath}>
                        <InputNumber size="small" style={{width: '100%'}}/>
                    </Form.Item>
                </td>
                <td>
                    <Form.Item noStyle name={yearBeforeThat.namesPath}>
                        <InputNumber size="small" style={{width: '100%'}}/>
                    </Form.Item>
                </td>
                <td>
                    <Form.Item noStyle name={guaranteed.namesPath} valuePropName="checked">
                        <Checkbox/>
                    </Form.Item>
                </td>

            </tr>
            </tbody>
        </table>
        {/*{open && item.methodology?.length && (*/}
        {/*    <table>*/}
        {/*        <colgroup>*/}
        {/*            <col style={{width: '60%'}}/>*/}
        {/*            <col style={{width: '32%'}}/>*/}
        {/*            <col style={{width: '8%'}}/>*/}
        {/*        </colgroup>*/}
        {/*        <tbody>*/}
        {/*        {item.methodology.map((row, index) => {*/}

        {/*            const borderTopStyle = row.operator === '=' ? {borderTop: '1px solid #ccc'} : {};*/}
        {/*            const borderTopAlignRightStyle = {...borderTopStyle, textAlign: 'right'};*/}
        {/*            return (*/}
        {/*                <tr key={index} style={borderTopStyle}>*/}
        {/*                    <td>*/}
        {/*                        <>*/}
        {/*                            {row.warning && <Popover content={row.warning}><ExclamationCircleFilled/></Popover>}*/}
        {/*                            {row.uploadID ? <a onClick={() => openUpload(row.uploadID)}>{row.description}</a> : row.description}*/}
        {/*                        </>*/}

        {/*                    </td>*/}
        {/*                    <td style={borderTopAlignRightStyle}>{row.amount}</td>*/}
        {/*                    <td style={alignCellStyle}>{row.operator}</td>*/}
        {/*                </tr>*/}
        {/*            )*/}
        {/*        })}*/}

        {/*        </tbody>*/}
        {/*    </table>*/}
        {/*)}*/}


    </div>)
}

export default Applicant;